<template>
  <v-card class="batch-edit">
    <!-- Title bar -->
    <div class="d-flex align-center justify-space-between batch-edit__title">
      <p class="batch-edit__title--left mb-0">
        {{ targetCount }} Assignment{{ targetCount === 1 ? '' : 's' }} | Batch Edit
      </p>
      <div class="d-flex align-center batch-edit__title--right">
        <button
          class="batch-edit__title--discard"
          :disabled="!touchedKeys.length || processing"
          @click.prevent="discardChanges"
        >
          Discard Changes
        </button>
        <button
          class="batch-edit__title--publish ml-2"
          :disabled="!touchedKeys.length || processing"
          @click.prevent="publishChanges"
        >
          {{
            processing
              ? 'Publishing…'
              : `Publish Changes | ${targetCount} Assignment${targetCount === 1 ? '' : 's'}`
          }}
        </button>
        <div class="batch-edit__title--delimetr ml-5"></div>
        <div class="batch-edit__title--icon ml-5">
          <v-btn text small disabled title="Not available in batch edit">
            <pmdashboard_table_icons :icon="'pmdashboard_slack'" />
          </v-btn>
        </div>
        <div class="batch-edit__title--icon ml-5">
          <v-btn text small disabled title="Not available in batch edit">
            <pmdashboard_table_icons :icon="'pmdashboard_ellipsis-solid'" />
          </v-btn>
        </div>
        <div class="batch-edit__title--close ml-2">
          <v-btn text small @click="closeForm">
            <pmdashboard_table_icons :icon="'pmdashboard_close_black'" />
          </v-btn>
        </div>
      </div>
    </div>

    <v-card-text class="batch-edit__body">
      <!-- Tabs. Only MAIN carries batch-editable content; the other two exist in
           the design but have no defined multi-assignment behaviour. -->
      <div class="batch-edit__tabs">
        <button
          v-for="tab in tabs"
          :key="tab.label"
          class="tab-btn"
          :class="{ active: tab.key === 'main' }"
          :disabled="tab.key !== 'main'"
          :title="tab.key !== 'main' ? 'Not available in batch edit' : ''"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- MAIN fields -->
      <div class="batch-edit__fields">
        <div
          v-for="(column, colIndex) in [leftFields, rightFields]"
          :key="colIndex"
          class="batch-edit__column"
        >
          <div v-for="field in column" :key="field.key" class="batch-edit__line">
            <!-- Label -->
            <div class="batch-edit__label">
              <ad_form_icons
                v-if="field.inherited"
                :icon="'pmdashboard_arrow_bottom_right'"
                class="batch-edit__label--inherit"
              />
              <component
                v-if="field.iconComp !== 'mdi'"
                :is="field.iconComp"
                :icon="field.icon"
                class="batch-edit__label--icon"
              />
              <v-icon v-else class="batch-edit__label--icon">{{ field.icon }}</v-icon>
              <p class="batch-edit__label--text mb-0">
                {{ field.label }}
                <span v-if="field.optional" class="batch-edit__label--optional">(opt.)</span>
              </p>
            </div>

            <!-- Value -->
            <div class="batch-edit__value">
              <!-- Read-only aggregate (Parent Task) -->
              <div v-if="!field.editable" class="batch-field batch-field--readonly">
                <span class="batch-field__text" :class="valueClass(field)">
                  {{ displayValue(field) }}
                </span>
              </div>

              <!-- Billed -->
              <div v-else-if="field.type === 'boolean'" class="batch-field">
                <v-checkbox
                  class="batch-field__checkbox mt-0 pt-0"
                  hide-details
                  :input-value="booleanValue(field)"
                  :indeterminate="isMixed(field)"
                  @change="setField(field.key, !!$event)"
                ></v-checkbox>
              </div>

              <!-- Status (chip) -->
              <v-menu v-else-if="field.type === 'status'" offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <v-chip
                      v-if="!isPlaceholder(field)"
                      class="ma-0 batch-field__status-chip"
                      :color="statusColor(rawValue(field))"
                      label
                    >
                      {{ displayValue(field) }}
                    </v-chip>
                    <span v-else class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item
                    v-for="opt in statusValues"
                    :key="opt"
                    @click="setField(field.key, opt)"
                  >
                    <v-list-item-title>
                      <v-icon left small :color="statusColor(opt)">mdi-circle</v-icon>
                      {{ opt }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Priority (indicator + label) -->
              <v-menu v-else-if="field.type === 'priority'" offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <v-icon
                      v-if="!isPlaceholder(field)"
                      class="batch-field__priority-icon"
                      :color="priorityMeta(rawValue(field)).color"
                    >
                      {{ priorityMeta(rawValue(field)).icon }}
                    </v-icon>
                    <span class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item
                    v-for="opt in priorities"
                    :key="opt"
                    @click="setField(field.key, opt)"
                  >
                    <v-list-item-title>
                      <v-icon left :color="priorityMeta(opt).color">{{ priorityMeta(opt).icon }}</v-icon>
                      {{ opt }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- PM (avatar + name) -->
              <v-menu v-else-if="field.type === 'employee'" offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <p
                      v-if="!isPlaceholder(field)"
                      class="batch-field__initials mb-0"
                      :style="{ 'background-color': initialsColor(selectedEmployee(field)) }"
                    >
                      {{ initialsOf(selectedEmployee(field)) }}
                    </p>
                    <span class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item
                    v-for="(opt, i) in optionsFor(field)"
                    :key="i"
                    @click="selectObject(field, opt)"
                  >
                    <v-list-item-title class="d-flex align-center">
                      <v-chip
                        class="mr-2 batch-field__dropdown-chip"
                        :color="initialsColor(opt)"
                        text-color="white"
                      >
                        {{ initialsOf(opt) }}
                      </v-chip>
                      {{ labelOf(field, opt) }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!optionsFor(field).length" disabled>
                    <v-list-item-title class="batch-field--placeholder">No options</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- File Location (link) -->
              <v-menu v-else-if="field.type === 'link'" offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <a
                      v-if="!isPlaceholder(field) && fileLocationLink(rawValue(field))"
                      class="batch-field__link"
                      target="_blank"
                      :href="fileLocationLink(rawValue(field))"
                      @click.stop
                    >
                      {{ displayValue(field) }}
                    </a>
                    <span v-else class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item
                    v-for="opt in fileLocationOptions"
                    :key="opt.label"
                    @click="setField(field.key, opt.label)"
                  >
                    <v-list-item-title>{{ opt.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Select (plain string options: Media Type / Keyword) -->
              <v-menu v-else-if="field.type === 'select'" offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <span class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item v-for="opt in optionsFor(field)" :key="opt" @click="setField(field.key, opt)">
                    <v-list-item-title>{{ opt }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!optionsFor(field).length" disabled>
                    <v-list-item-title class="batch-field--placeholder">No options</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Object select (Project / Department / Client Req. / PO) -->
              <v-menu
                v-else-if="field.type === 'objectSelect'"
                offset-y
                :close-on-content-click="true"
                :disabled="field.needsProject && !canEditProjectScoped"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="batch-field"
                    :class="
                      field.needsProject && !canEditProjectScoped
                        ? 'batch-field--locked'
                        : 'batch-field--clickable'
                    "
                    v-on="on"
                    v-bind="attrs"
                    :title="
                      field.needsProject && !canEditProjectScoped
                        ? 'Select a single Project across all rows to edit this'
                        : ''
                    "
                  >
                    <span class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-list class="batch-field__list">
                  <v-list-item
                    v-for="(opt, i) in optionsFor(field)"
                    :key="i"
                    @click="selectObject(field, opt)"
                  >
                    <v-list-item-title>{{ labelOf(field, opt) }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!optionsFor(field).length" disabled>
                    <v-list-item-title class="batch-field--placeholder">No options</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Pay Period (month) -->
              <v-menu v-else-if="field.type === 'month'" offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <div class="batch-field batch-field--clickable" v-on="on" v-bind="attrs">
                    <span class="batch-field__text" :class="valueClass(field)">
                      {{ displayValue(field) }}
                    </span>
                    <span
                      v-if="isTouched(field.key)"
                      class="batch-field__clear"
                      @click.stop="revertField(field.key)"
                    >
                      <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                    </span>
                  </div>
                </template>
                <v-card>
                  <v-date-picker
                    type="month"
                    :value="isTouched(field.key) ? form[field.key] : null"
                    @input="setField(field.key, $event)"
                  ></v-date-picker>
                </v-card>
              </v-menu>

              <!-- Text / Number -->
              <div v-else class="batch-field">
                <input
                  class="batch-field__input"
                  :type="field.type === 'number' ? 'number' : 'text'"
                  :value="editableText(field)"
                  :placeholder="isMixed(field) ? 'Different Values' : 'Empty'"
                  @input="setField(field.key, $event.target.value)"
                />
                <span
                  v-if="isTouched(field.key)"
                  class="batch-field__clear"
                  @click.stop="revertField(field.key)"
                >
                  <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ASSIGNMENT STEPS -->
      <p class="batch-edit__section-title">Assignment Steps</p>
      <div class="batch-edit__panel">
        <!-- Timezones -->
        <div class="batch-edit__timezones">
          <div
            class="batch-edit__tz-chip"
            :class="{ 'batch-edit__tz-chip--active': activeTimeZone === 'MyTime' }"
          >
            <span class="batch-edit__tz-chip--label" @click="activeTimeZone = 'MyTime'">
              {{ myTime.label }}
            </span>
            <v-menu offset-y :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="batch-edit__tz-chip--arrow" v-on="on" v-bind="attrs">
                  mdi-chevron-down
                </v-icon>
              </template>
              <v-list class="batch-field__list">
                <v-list-item v-for="opt in myTimeZoneOptions" :key="opt.label" @click="myTime = opt">
                  <v-list-item-title>{{ opt.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div
            class="batch-edit__tz-chip"
            :class="{ 'batch-edit__tz-chip--active': activeTimeZone === 'ClientTime' }"
          >
            <span class="batch-edit__tz-chip--label" @click="activeTimeZone = 'ClientTime'">
              {{ clientTime.label }}
            </span>
            <v-menu offset-y :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="batch-edit__tz-chip--arrow" v-on="on" v-bind="attrs">
                  mdi-chevron-down
                </v-icon>
              </template>
              <v-list class="batch-field__list">
                <v-list-item
                  v-for="opt in clientTimeZoneOptions"
                  :key="opt.label"
                  @click="clientTime = opt"
                >
                  <v-list-item-title>{{ opt.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <!-- Dates + overrides -->
        <div class="batch-edit__panel-grid">
          <div class="batch-edit__panel-col">
            <div v-for="pair in datePairs" :key="pair.key" class="batch-edit__line">
              <div class="batch-edit__label batch-edit__label--wide">
                <pmdashboard_table_icons :icon="pair.icon" class="batch-edit__label--icon" />
                <p class="batch-edit__label--text mb-0">{{ pair.label }}</p>
              </div>
              <div class="batch-edit__value">
                <v-menu
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div class="batch-field batch-field--boxed" v-on="on" v-bind="attrs">
                      <span class="batch-field__text" :class="dateValueClass(pair)">
                        {{ dateDisplayValue(pair) }}
                      </span>
                      <span
                        v-if="isTouched(pair.dateKey) || isTouched(pair.timeKey)"
                        class="batch-field__clear"
                        @click.stop="revertPair(pair)"
                      >
                        <pmdashboard_table_icons :icon="'pmdashboard_clear_text_field'" class="d-block" />
                      </span>
                    </div>
                  </template>
                  <CustomDateTimePicker
                    :preparedDate="pairDate(pair)"
                    :preparedTime="pairTime(pair)"
                    @onDateSelected="setField(pair.dateKey, $event)"
                    @onTimeSelected="onPairTimeSelected(pair, $event)"
                  />
                </v-menu>
              </div>
            </div>
          </div>
          <div class="batch-edit__panel-col">
            <div v-for="ovr in overrides" :key="ovr.key" class="batch-edit__line">
              <div class="batch-edit__label batch-edit__label--wide">
                <ad_form_icons :icon="ovr.icon" class="batch-edit__label--icon" />
                <p class="batch-edit__label--text mb-0">{{ ovr.label }}</p>
              </div>
              <div class="batch-edit__value">
                <div class="batch-field batch-field--boxed">
                  <v-checkbox
                    class="batch-field__checkbox mt-0 pt-0"
                    hide-details
                    v-model="overrideFlags[ovr.key]"
                  ></v-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Steps table -->
        <div class="batch-edit__table batch-edit__table--steps">
          <div class="batch-edit__table--row batch-edit__table--head">
            <div class="batch-edit__table--cell">Step</div>
            <div class="batch-edit__table--cell">Person</div>
            <div class="batch-edit__table--cell">Due</div>
            <div class="batch-edit__table--cell">Accepted</div>
          </div>
          <div v-for="step in stepRows" :key="step.label" class="batch-edit__table--row">
            <div class="batch-edit__table--cell">{{ step.label }}</div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': step.personMixed }">
              {{ step.person }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': step.dueMixed }">
              {{ step.due }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': step.acceptedMixed }">
              {{ step.accepted }}
            </div>
          </div>
        </div>

        <div class="batch-edit__panel-actions">
          <v-btn
            small
            outlined
            color="#1976D2"
            disabled
            title="Steps are edited per assignment"
          >
            Add Step
          </v-btn>
        </div>
      </div>

      <!-- ASSIGNMENT WORK TYPES -->
      <p class="batch-edit__section-title">Assignment Work Types</p>
      <div class="batch-edit__panel">
        <div class="batch-edit__table batch-edit__table--worktypes">
          <div class="batch-edit__table--row batch-edit__table--head">
            <div class="batch-edit__table--cell">Work Type</div>
            <div class="batch-edit__table--cell">Work Type Reference</div>
            <div class="batch-edit__table--cell">Quantity</div>
            <div class="batch-edit__table--cell">Primary Rate</div>
            <div class="batch-edit__table--cell">Secondary Rate</div>
            <div class="batch-edit__table--cell">Client Rate</div>
            <div class="batch-edit__table--cell">Status</div>
            <div class="batch-edit__table--cell"></div>
          </div>

          <div v-for="row in workTypeRows" :key="row.label" class="batch-edit__table--row">
            <div class="batch-edit__table--cell">{{ row.label }}</div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': row.referenceMixed }">
              <span
                v-if="!row.referenceMixed && row.reference !== '-'"
                class="batch-edit__table--reference"
                @click="checkPrice(row.category, row.reference)"
              >
                {{ row.reference }}
              </span>
              <template v-else>{{ row.reference }}</template>
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': row.quantityMixed }">
              {{ row.quantity }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': row.primaryRateMixed }">
              {{ row.primaryRate }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': row.secondaryRateMixed }">
              {{ row.secondaryRate }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': row.clientRateMixed }">
              {{ row.clientRate }}
            </div>
            <div class="batch-edit__table--cell">-</div>
            <div class="batch-edit__table--cell"></div>
          </div>

          <div v-if="!workTypeRows.length" class="batch-edit__table--row">
            <div class="batch-edit__table--cell batch-field--placeholder">No work types</div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
          </div>

          <!-- TOTAL -->
          <div v-if="workTypeRows.length" class="batch-edit__table--row batch-edit__table--total">
            <div class="batch-edit__table--cell">TOTAL</div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': totals.primaryMixed }">
              {{ totals.primary }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': totals.secondaryMixed }">
              {{ totals.secondary }}
            </div>
            <div class="batch-edit__table--cell" :class="{ 'batch-field--placeholder': totals.clientMixed }">
              {{ totals.client }}
            </div>
            <div class="batch-edit__table--cell"></div>
            <div class="batch-edit__table--cell"></div>
          </div>
        </div>

        <div class="batch-edit__panel-actions">
          <v-btn
            small
            outlined
            color="#1976D2"
            class="mr-2"
            disabled
            title="Work types are edited per assignment"
          >
            Add Work Type
          </v-btn>
          <v-btn
            small
            outlined
            color="#1976D2"
            disabled
            title="Work time is logged per assignment"
          >
            Log Work Time
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { parseAssignmentResponse } from "@/utils/assignmentUtils.js";
import {
  processAssignment,
  getOneProject,
  updateAssignmentsList,
} from "@/utils/newDbUtils.js";
import {
  STATUS_COLORS,
  ROUTES,
  DOMAIN,
  alphabetColors,
  managersDesiredColors,
  fileLocationDropdownOptions,
} from "@/utils/constants.js";
import CustomDateTimePicker from "@/components/ui/CustomDateTimePicker.vue";
import pmdashboard_table_icons from "@/assets/icons/pmdashboard_table_icons.vue";
import ad_form_icons from "@/assets/icons/ad_form_icons.vue";
import ad_filters_icons from "@/assets/icons/ad_filters_icons.vue";

// MAIN-tab fields, in the order and columns the CueHub design lays them out.
// `key` is the flat assignment-form field (parseAssignmentResponse); `backendKey`
// is the dotted path in the /assignment/part-update payload — the same shape
// formatBackendData produces (nested assignmentDetails/schedule/pricing + top-level
// entity IDs). `objectSelect`/`employee` fields store the selected option object
// and resolve to an ID via `idPath`. `inherited` renders the ↳ marker the design
// uses for values that come down from the parent task. `needsProject` fields
// (Client Req. / PO) are only editable when every selected row shares one project,
// since their option lists are project-scoped. Parent Task is task-level and stays
// read-only.
const FIELD_DEFS = {
  taskName:         { key: "taskName",         label: "Parent Task",     iconComp: "ad_filters_icons",        icon: "arrow_top_bold",              column: "left",  editable: false, type: "text" },
  relatedProject:   { key: "relatedProject",   label: "Project",         iconComp: "ad_form_icons",           icon: "pmdashboard_book",            column: "left",  editable: true,  type: "objectSelect", optionsKey: "projects",         idPath: "id",      backendKey: "projectId",              inherited: true },
  department:       { key: "department",       label: "Department",      iconComp: "mdi",                     icon: "mdi-sitemap-outline",         column: "left",  editable: true,  type: "objectSelect", optionsKey: "departments",      idPath: "id",      backendKey: "department",             inherited: true },
  clientRequester:  { key: "clientRequester",  label: "Client Req.",     iconComp: "pmdashboard_table_icons", icon: "pmdashboard_account",         column: "left",  editable: true,  type: "objectSelect", optionsKey: "clientRequesters", idPath: "user.id", backendKey: "clientRequesterId",      inherited: true, needsProject: true },
  payPeriod:        { key: "payPeriod",        label: "Pay Period",      iconComp: "ad_filters_icons",        icon: "money-bill-wave",             column: "left",  editable: true,  type: "month",                                                     backendKey: "schedule.payPeriod",     inherited: true },
  purchaseOrder:    { key: "purchaseOrder",    label: "Purchase Order",  iconComp: "ad_form_icons",           icon: "adashboard_PO",               column: "left",  editable: true,  type: "objectSelect", optionsKey: "purchaseOrders",   idPath: "id",      backendKey: "pricing.purchaseOrderId", needsProject: true },
  status:           { key: "status",           label: "Status",          iconComp: "pmdashboard_table_icons", icon: "pmdashboard_status",          column: "left",  editable: true,  type: "status",                                                    backendKey: "assignmentDetails.assignmentStatus" },
  isBilled:         { key: "isBilled",         label: "Billed",          iconComp: "pmdashboard_table_icons", icon: "pmdashboard_billed",          column: "left",  editable: true,  type: "boolean",                                                   backendKey: "assignmentDetails.billed" },
  projectManager:   { key: "projectManager",   label: "PM",              iconComp: "pmdashboard_table_icons", icon: "pmdashboard_account",         column: "left",  editable: true,  type: "employee",     optionsKey: "pmOptions",        idPath: "user.id", backendKey: "projectManagerId" },

  priority:         { key: "priority",         label: "Priority",        iconComp: "pmdashboard_table_icons", icon: "pmdashboard_priority_flag",   column: "right", editable: true,  type: "priority",                                                  backendKey: "assignmentDetails.priority", optional: true },
  fileLocation:     { key: "fileLocation",     label: "File Location",   iconComp: "pmdashboard_table_icons", icon: "pmdashboard_fileLocation",    column: "right", editable: true,  type: "link",                                                      backendKey: "assignmentDetails.fileLocation", inherited: true },
  emailSubjectLine: { key: "emailSubjectLine", label: "Email Subject Line", iconComp: "ad_form_icons",        icon: "email_subject_line",          column: "right", editable: true,  type: "text",                                                      backendKey: "assignmentDetails.emailSubjectLine" },
  mediaType:        { key: "mediaType",        label: "Media Type",      iconComp: "ad_form_icons",           icon: "media_type",                  column: "right", editable: true,  type: "select",       optionsKey: "mediaTypes",                          backendKey: "assignmentDetails.mediaType" },
  keyword:          { key: "keyword",          label: "Keyword",         iconComp: "ad_form_icons",           icon: "keyword",                     column: "right", editable: true,  type: "select",       optionsKey: "keywords",                            backendKey: "assignmentDetails.keyword" },
  genre:            { key: "genre",            label: "Genre",           iconComp: "ad_form_icons",           icon: "genre",                       column: "right", editable: true,  type: "text",                                                      backendKey: "assignmentDetails.genre" },
  wordCount:        { key: "wordCount",        label: "Word Count",      iconComp: "ad_form_icons",           icon: "word_count",                  column: "right", editable: true,  type: "number",                                                    backendKey: "assignmentDetails.wordCount" },
  characterCount:   { key: "characterCount",   label: "Character Count", iconComp: "ad_form_icons",           icon: "word_count",                  column: "right", editable: true,  type: "number",                                                    backendKey: "assignmentDetails.characterCount" },
};

const FIELDS = Object.values(FIELD_DEFS);
const MIXED = "__MIXED__"; // sentinel: selected rows disagree on this field

// Request / Delivery are date+time pairs that publish as two schedule keys but
// read as one value in the UI.
const DATE_PAIRS = [
  {
    key: "requestDateTime",
    label: "Request Date/Time",
    icon: "pmdashboard_request_date",
    dateKey: "requestDate",
    timeKey: "requestTime",
    dateBackendKey: "schedule.requestDate",
    timeBackendKey: "schedule.requestTime",
  },
  {
    key: "deliveryDateTime",
    label: "Delivery Date/Time",
    icon: "pmdashboard_deliveryDate",
    dateKey: "deliveryDate",
    timeKey: "deliveryTime",
    dateBackendKey: "schedule.deliveryDate",
    timeBackendKey: "schedule.deliveryTime",
  },
];

const DATE_PAIR_KEYS = DATE_PAIRS.reduce((acc, pair) => {
  acc[pair.dateKey] = pair.dateBackendKey;
  acc[pair.timeKey] = pair.timeBackendKey;
  return acc;
}, {});

// Assignment steps, in the order the design lists them.
const STEP_DEFS = [
  { label: "Primary",   personKey: "primary",   dateKey: "primaryDueDate",   timeKey: "primaryDueTime",   acceptedKey: "primaryAccepted" },
  { label: "Secondary", personKey: "secondary", dateKey: "secondaryDueDate", timeKey: "secondaryDueTime", acceptedKey: "secondaryAccepted" },
  { label: "QC",        personKey: "qc",        dateKey: "qcDueDate",        timeKey: "qcDueTime",        acceptedKey: null },
];

const TIME_ZONES = [
  "PST-08:00",
  "PDT-07:00",
  "CST-06:00",
  "EST-05:00",
  "EDT-04:00",
  "GMT+00:00",
  "CET+01:00",
  "CEST+02:00",
  "EET+02:00",
  "EEST+03:00",
  "GST+04:00",
  "HKT+08:00",
];

export default {
  name: "ADBatchEditModal",

  components: {
    CustomDateTimePicker,
    pmdashboard_table_icons,
    ad_form_icons,
    ad_filters_icons,
  },

  props: {
    // Backend-shaped assignment rows currently checked in the table.
    selectedRows: { type: Array, required: true },
    statuses: { type: Array, default: () => [] }, // [{ value }] or [string]
    mediaTypes: { type: Array, default: () => [] }, // [string]
    keywords: { type: Array, default: () => [] }, // [string]
    projects: { type: Array, default: () => [] }, // [{ id, projectName }]
    departments: { type: Array, default: () => [] }, // [{ id, value }] from getSpecificEnum("departments")
    staffList: { type: Array, default: () => [] }, // [{ user: { id, firstName, lastName, userRole } }]
    // "All matching" scope from the table header: publish against the active
    // filter query server-side instead of the loaded rows.
    bulkQueryEdit: { type: Boolean, default: false },
    matchingCount: { type: Number, default: 0 },
    filterQuery: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      // Per-field aggregate of the selection: a shared value, "" for all-empty,
      // or the MIXED sentinel when the rows disagree.
      aggregate: {},
      // User overrides. A key present in `touched` is applied to every selected
      // assignment on publish.
      form: {},
      touched: {},
      processing: false,
      // Full project detail (with clientRequesters / purchaseOrders) loaded when
      // the selection shares one project or the user picks a Project.
      projectDetail: null,
      loadingProject: false,
      tabs: [
        { key: "main", label: "MAIN 📋" },
        { key: "notes", label: "NOTES & FILES 📁" },
        { key: "deliverables", label: "DELIVERABLES 📦" },
      ],
      priorities: ["High", "Medium", "Low"],
      fileLocationOptions: fileLocationDropdownOptions,
      datePairs: DATE_PAIRS,
      overrides: [
        { key: "overrideStaffAvailability", label: "Override Staff Availability", icon: "clock_fast" },
        { key: "overrideDueDates", label: "Override Due Dates", icon: "calendar_close" },
      ],
      // Front-end-only flags, exactly as in the single-assignment form — no
      // backend field exists for either, so they are never published.
      overrideFlags: {
        overrideStaffAvailability: false,
        overrideDueDates: false,
      },
      activeTimeZone: "ClientTime",
      myTime: { label: "My (EDT-04:00)", value: "-04:00" },
      clientTime: { label: "Client (EDT-04:00)", value: "-04:00" },
    };
  },

  computed: {
    ...mapGetters("auth", ["isAdmin"]),
    rows() {
      return this.selectedRows || [];
    },
    // How many assignments the publish will touch. Under "All matching" that is
    // every record the filters return, not just the rows loaded on this page.
    targetCount() {
      return this.bulkQueryEdit ? this.matchingCount : this.rows.length;
    },
    parsedRows() {
      return this.rows.map((row) => parseAssignmentResponse(row));
    },
    touchedKeys() {
      return Object.keys(this.touched);
    },
    leftFields() {
      return FIELDS.filter((f) => f.column === "left");
    },
    rightFields() {
      return FIELDS.filter((f) => f.column === "right");
    },
    statusValues() {
      return this.statuses.map((s) => (typeof s === "string" ? s : s.value));
    },
    pmOptions() {
      return (this.staffList || [])
        .filter((el) => el.user && el.user.userRole === "MANAGER")
        .map((el) => ({
          ...el,
          name: `${el.user.firstName || ""} ${el.user.lastName || ""}`.trim(),
        }));
    },
    myTimeZoneOptions() {
      return TIME_ZONES.map((tz) => ({ label: `My (${tz})`, value: tz.slice(-6) }));
    },
    clientTimeZoneOptions() {
      return TIME_ZONES.map((tz) => ({ label: `Client (${tz})`, value: tz.slice(-6) }));
    },
    // The project the client-scoped fields resolve against: a user-picked
    // Project, else the project shared by every selected row (null if mixed).
    sharedProjectId() {
      const ids = this.parsedRows.map((r) => r.relatedProject && r.relatedProject.id);
      if (!ids.length || ids.some((id) => !id)) return null;
      return ids.every((id) => id === ids[0]) ? ids[0] : null;
    },
    canEditProjectScoped() {
      return !!this.projectDetail;
    },
    optionSources() {
      return {
        mediaTypes: this.mediaTypes,
        keywords: this.keywords,
        projects: this.projects,
        departments: this.departments,
        pmOptions: this.pmOptions,
        clientRequesters: (this.projectDetail && this.projectDetail.clientRequesters) || [],
        purchaseOrders: (this.projectDetail && this.projectDetail.purchaseOrders) || [],
      };
    },
    // Per-step aggregate of Person / Due / Accepted across the selection.
    stepRows() {
      return STEP_DEFS.map((step) => {
        const people = this.parsedRows.map((r) => this.personName(r[step.personKey]));
        const dues = this.parsedRows.map((r) =>
          this.formatDateTime(r.schedule && r.schedule[step.dateKey], r.schedule && r.schedule[step.timeKey])
        );
        const accepted = step.acceptedKey
          ? this.rows.map((raw) => {
              const value = raw.assignmentDetails && raw.assignmentDetails[step.acceptedKey];
              return value ? String(value) : "";
            })
          : [""];
        const person = this.aggregateOf(people);
        const due = this.aggregateOf(dues);
        const accept = this.aggregateOf(accepted);
        return {
          label: step.label,
          person: this.cellText(person),
          personMixed: person === MIXED || person === "",
          due: this.cellText(due),
          dueMixed: due === MIXED || due === "",
          accepted: this.cellText(accept),
          acceptedMixed: accept === MIXED || accept === "",
        };
      });
    },
    // One row per work-type position, each column aggregated across the selection.
    workTypeRows() {
      const lists = this.parsedRows.map((r) =>
        [...((r.pricing && r.pricing.workTypes) || [])].sort(
          (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
        )
      );
      const longest = lists.reduce((max, list) => Math.max(max, list.length), 0);
      const rows = [];
      for (let i = 0; i < longest; i++) {
        const items = lists.map((list) => list[i] || null);
        const first = items.find((el) => el && el.customerPriceList);
        const reference = this.aggregateOf(
          items.map((el) => (el && el.customerPriceList && el.customerPriceList.workTypeName) || "")
        );
        const quantity = this.aggregateOf(
          items.map((el) => (el && el.quantity !== null && el.quantity !== undefined ? String(el.quantity) : ""))
        );
        const primaryRate = this.aggregateOf(
          items.map((el) => this.money(el && el.customerPriceList && el.customerPriceList.primaryPriceCard))
        );
        const secondaryRate = this.aggregateOf(
          items.map((el) => this.money(el && el.customerPriceList && el.customerPriceList.secondaryPriceCard))
        );
        const clientRate = this.aggregateOf(
          items.map((el) => this.money(el && el.customerPriceList && el.customerPriceList.clientPriceCard))
        );
        rows.push({
          label: `Work Type ${i + 1}`,
          category: (first && first.customerPriceList && first.customerPriceList.workTypeCategory) || null,
          reference: this.cellText(reference),
          referenceMixed: reference === MIXED || reference === "",
          quantity: this.cellText(quantity),
          quantityMixed: quantity === MIXED || quantity === "",
          primaryRate: this.cellText(primaryRate),
          primaryRateMixed: primaryRate === MIXED,
          secondaryRate: this.cellText(secondaryRate),
          secondaryRateMixed: secondaryRate === MIXED,
          clientRate: this.cellText(clientRate),
          clientRateMixed: clientRate === MIXED,
        });
      }
      return rows;
    },
    totals() {
      const pick = (key) =>
        this.aggregateOf(this.parsedRows.map((r) => this.money(r.pricing && r.pricing[key])));
      const primary = pick("primaryTotal");
      const secondary = pick("secondaryTotal");
      const client = pick("clientTotal");
      return {
        primary: this.cellText(primary),
        primaryMixed: primary === MIXED,
        secondary: this.cellText(secondary),
        secondaryMixed: secondary === MIXED,
        client: this.cellText(client),
        clientMixed: client === MIXED,
      };
    },
  },

  created() {
    this.computeAggregates();
    if (this.sharedProjectId) this.loadProjectDetail(this.sharedProjectId);
  },

  methods: {
    ...mapActions("flashMessage", ["handleFlash"]),

    // Reduce a list of values down to the shared one, or MIXED when they differ.
    aggregateOf(values) {
      if (!values.length) return "";
      const first = values[0];
      return values.every((v) => v === first) ? first : MIXED;
    },

    // Reduce the selection down to one representative value per field, or MIXED.
    computeAggregates() {
      const agg = {};
      FIELDS.forEach((field) => {
        agg[field.key] = this.aggregateOf(
          this.parsedRows.map((parsed, i) => this.readValue(parsed, this.rows[i], field))
        );
      });
      // Request / Delivery date and time aggregate per key so a touched pair can
      // publish both halves independently.
      DATE_PAIRS.forEach((pair) => {
        agg[pair.dateKey] = this.aggregateOf(this.parsedRows.map((r) => r[pair.dateKey] || ""));
        agg[pair.timeKey] = this.aggregateOf(this.parsedRows.map((r) => r[pair.timeKey] || ""));
      });
      this.aggregate = agg;
    },

    // Extract a comparable display value for a field from a parsed row. Falls
    // back to the raw backend row for fields parseAssignmentResponse omits.
    readValue(row, raw, field) {
      switch (field.key) {
        // The /assignment payload carries no parent-task title — only its ID —
        // and fetching one task per selected row is not worth it here, so the
        // read-only Parent Task cell shows the Cue Task ID.
        case "taskName":
          return row.taskId || "";
        case "relatedProject":
          return row.relatedProject && row.relatedProject.name !== "Empty"
            ? row.relatedProject.name
            : "";
        case "department":
          return (raw && raw.department && (raw.department.value || raw.department.name)) || "";
        case "clientRequester": {
          const u = row.clientRequester && row.clientRequester.user;
          return u && u.firstName !== "Empty"
            ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
            : "";
        }
        case "purchaseOrder":
          return row.purchaseOrder ? row.purchaseOrder.purchaseOrderNumber : "";
        case "projectManager":
          return row.projectManager ? row.projectManager.name : "";
        default:
          if (field.type === "boolean") return !!row[field.key];
          return row[field.key] === null || row[field.key] === undefined ? "" : row[field.key];
      }
    },

    isTouched(key) {
      return Object.prototype.hasOwnProperty.call(this.touched, key);
    },

    isMixed(field) {
      return !this.isTouched(field.key) && this.aggregate[field.key] === MIXED;
    },

    // The underlying value behind the displayed one — used by the chip / link /
    // indicator renderers that need more than the label.
    rawValue(field) {
      if (this.isTouched(field.key)) return this.form[field.key];
      const agg = this.aggregate[field.key];
      return agg === MIXED ? null : agg;
    },

    booleanValue(field) {
      if (this.isTouched(field.key)) return !!this.form[field.key];
      return this.aggregate[field.key] === MIXED ? false : !!this.aggregate[field.key];
    },

    // Raw value for text/number inputs (unformatted).
    editableText(field) {
      if (this.isTouched(field.key)) return this.form[field.key];
      const agg = this.aggregate[field.key];
      return agg === MIXED ? "" : agg;
    },

    // Display label for an entity option / selected object.
    labelOf(field, obj) {
      if (!obj) return "";
      switch (field.key) {
        case "relatedProject":
          return obj.projectName || obj.name || "";
        case "department":
          return obj.value || obj.name || "";
        case "clientRequester":
          return obj.user
            ? `${obj.user.firstName || ""} ${obj.user.lastName || ""}`.trim()
            : obj.name || "";
        case "purchaseOrder":
          return obj.purchaseOrderNumber || "";
        case "projectManager":
          return (
            obj.name ||
            (obj.user ? `${obj.user.firstName || ""} ${obj.user.lastName || ""}`.trim() : "")
          );
        default:
          return String(obj);
      }
    },

    // Formatted value for read-only / activator display.
    displayValue(field) {
      if (this.isTouched(field.key)) {
        if (field.type === "objectSelect" || field.type === "employee")
          return this.labelOf(field, this.form[field.key]) || "Empty";
        return this.formatValue(field, this.form[field.key]);
      }
      const agg = this.aggregate[field.key];
      if (agg === MIXED) return "Different Values";
      if (field.type === "boolean") return agg ? "Yes" : "No";
      return agg === "" || agg === null || agg === undefined ? "Empty" : this.formatValue(field, agg);
    },

    formatValue(field, value) {
      if (value === "" || value === null || value === undefined) return "Empty";
      if (field.type === "boolean") return value ? "Yes" : "No";
      if (field.type === "month") return this.formatPayPeriod(value);
      return value;
    },

    formatPayPeriod(value) {
      // Stored as "YYYY-MM"; show "Month YYYY".
      const m = this.$moment(value, "YYYY-MM");
      return m.isValid() ? m.format("MMMM YYYY") : value;
    },

    // Whether the displayed value should read as a muted placeholder.
    isPlaceholder(field) {
      if (this.isTouched(field.key)) {
        const v = this.form[field.key];
        return v === "" || v === null || v === undefined;
      }
      const agg = this.aggregate[field.key];
      return agg === MIXED || agg === "" || agg === null || agg === undefined;
    },

    valueClass(field) {
      return this.isPlaceholder(field) ? "batch-field--placeholder" : "batch-field--filled";
    },

    // The selected staff object behind an `employee` field, so the avatar can be
    // coloured even when the value came from the aggregate rather than a pick.
    selectedEmployee(field) {
      if (this.isTouched(field.key)) return this.form[field.key];
      const name = this.rawValue(field);
      if (!name) return null;
      return this.optionsFor(field).find((opt) => this.labelOf(field, opt) === name) || { name };
    },

    initialsOf(employee) {
      if (!employee) return "";
      if (employee.initials) return employee.initials;
      const user = employee.user || employee;
      const first = (user.firstName || employee.name || "").charAt(0);
      const last = (user.lastName || (employee.name || "").split(" ")[1] || "").charAt(0);
      return `${first}${last}`.toUpperCase();
    },

    // Same avatar palette the single-assignment form uses.
    initialsColor(employee) {
      if (!employee) return "#9747FF";
      const name = this.labelOf(FIELD_DEFS.projectManager, employee) || employee.name;
      const initials = this.initialsOf(employee);
      const desired = managersDesiredColors.find((el) => el.name === name);
      if (desired) return desired.color;
      return (
        (initials &&
          initials[1] &&
          alphabetColors.find((color) => color.character === initials[1])?.color) ||
        "#9747FF"
      );
    },

    statusColor(status) {
      return STATUS_COLORS.find((color) => color.statuses.includes(status))?.value || "#E0E0E0";
    },

    priorityMeta(priority) {
      if (priority === "High") return { icon: "mdi-menu-up", color: "#B13A41" };
      if (priority === "Medium") return { icon: "mdi-drag-horizontal-variant", color: "#CF940A" };
      if (priority === "Low") return { icon: "mdi-menu-down", color: "#4466FF" };
      return { icon: "", color: "" };
    },

    fileLocationLink(label) {
      return this.fileLocationOptions.find((opt) => opt.label === label)?.link || null;
    },

    setField(key, value) {
      this.$set(this.form, key, value);
      this.$set(this.touched, key, true);
    },

    // Pick an entity option; changing Project reloads project-scoped options and
    // drops any Client Req. / PO edits that belonged to the previous project.
    selectObject(field, option) {
      this.setField(field.key, option);
      if (field.key === "relatedProject") {
        this.revertField("clientRequester");
        this.revertField("purchaseOrder");
        this.loadProjectDetail(option && option.id);
      }
    },

    revertField(key) {
      this.$delete(this.form, key);
      this.$delete(this.touched, key);
    },

    optionsFor(field) {
      return (field.optionsKey && this.optionSources[field.optionsKey]) || [];
    },

    // --- Request / Delivery date-time pairs -------------------------------

    // The date/time picker expects "YYYY-MM-DD" / "HH:mm" strings, but the
    // schedule fields come back as either strings or [Y, M, D] / [H, M] tuples
    // depending on the endpoint, so normalise both before handing them over.
    dateToString(value) {
      if (!value) return null;
      if (!Array.isArray(value)) return String(value);
      const [y, m, d] = value;
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    },

    timeToString(value) {
      if (!value) return null;
      if (!Array.isArray(value)) return String(value);
      return value.map((part) => String(part).padStart(2, "0")).join(":");
    },

    pairDate(pair) {
      if (this.isTouched(pair.dateKey)) return this.dateToString(this.form[pair.dateKey]);
      const agg = this.aggregate[pair.dateKey];
      return agg === MIXED ? null : this.dateToString(agg);
    },

    pairTime(pair) {
      if (this.isTouched(pair.timeKey)) return this.timeToString(this.form[pair.timeKey]);
      const agg = this.aggregate[pair.timeKey];
      return agg === MIXED ? null : this.timeToString(agg);
    },

    pairIsMixed(pair) {
      return (
        (!this.isTouched(pair.dateKey) && this.aggregate[pair.dateKey] === MIXED) ||
        (!this.isTouched(pair.timeKey) && this.aggregate[pair.timeKey] === MIXED)
      );
    },

    dateDisplayValue(pair) {
      if (this.pairIsMixed(pair)) return "Different Values";
      return this.formatDateTime(this.pairDate(pair), this.pairTime(pair)) || "Empty";
    },

    dateValueClass(pair) {
      return this.pairIsMixed(pair) || !this.formatDateTime(this.pairDate(pair), this.pairTime(pair))
        ? "batch-field--placeholder"
        : "batch-field--filled";
    },

    // Mirrors the single-assignment form: the picker hands back "h:mm AM/PM",
    // the backend stores "HH:mm:ss".
    onPairTimeSelected(pair, timeAmPm) {
      if (!timeAmPm) return;
      const [time, modifier] = String(timeAmPm).split(" ");
      let [hours, minutes] = time.split(":");
      if (modifier === "PM" && hours !== "12") hours = parseInt(hours, 10) + 12;
      if (modifier === "AM" && hours === "12") hours = "00";
      hours = hours.toString().padStart(2, "0");
      this.setField(pair.timeKey, `${hours}:${minutes}:00`);
    },

    revertPair(pair) {
      this.revertField(pair.dateKey);
      this.revertField(pair.timeKey);
    },

    // --- Shared formatters ------------------------------------------------

    // "13 Jun 2024 (11:00 AM)" — the format the design and the assignment form use.
    formatDateTime(dateValue, timeValue) {
      if (!dateValue || !timeValue) return "";
      const dateStr = this.dateToString(dateValue);
      const timeStr = this.timeToString(timeValue);
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "PM" && hours !== 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
      const date = new Date(dateStr);
      if (isNaN(date)) return "";
      date.setHours(hours, minutes || 0, 0, 0);
      const datePart = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
      const timePart = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "UTC",
      });
      return `${datePart} (${timePart})`;
    },

    personName(person) {
      if (!person) return "";
      const user = person.user || person;
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    },

    money(value) {
      if (value === null || value === undefined || value === "") return "";
      return `$${Number(value).toFixed(2)}`;
    },

    // Aggregated table cells read "Different Values" when the rows disagree and
    // "-" when every row is empty.
    cellText(value) {
      if (value === MIXED) return "Different Values";
      return value === "" || value === null || value === undefined ? "-" : value;
    },

    checkPrice(workTypeCategory, workTypeName) {
      if (this.isAdmin)
        window.open(
          `${DOMAIN}/${ROUTES.customerPriceList}?WorkTypeCategory=${workTypeCategory}&Search=${workTypeName}`,
          "_blank"
        );
    },

    async loadProjectDetail(id) {
      if (!id) {
        this.projectDetail = null;
        return;
      }
      this.loadingProject = true;
      try {
        const resp = await getOneProject(id);
        this.projectDetail = resp && resp.data ? resp.data : null;
      } catch (err) {
        console.error(err);
        this.projectDetail = null;
      } finally {
        this.loadingProject = false;
      }
    },

    discardChanges() {
      this.form = {};
      this.touched = {};
    },

    getPath(obj, path) {
      return path.split(".").reduce((o, k) => (o === null || o === undefined ? o : o[k]), obj);
    },

    setPath(obj, path, value) {
      const keys = path.split(".");
      let cur = obj;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else {
          if (!cur[k] || typeof cur[k] !== "object") cur[k] = {};
          cur = cur[k];
        }
      });
    },

    // Build the /assignment/part-update payload from touched, editable fields.
    buildPayload() {
      const payload = {};
      this.touchedKeys.forEach((key) => {
        // Request / Delivery date+time halves.
        if (DATE_PAIR_KEYS[key]) {
          const value = this.form[key];
          this.setPath(payload, DATE_PAIR_KEYS[key], value === "" ? null : value);
          return;
        }
        const field = FIELD_DEFS[key];
        if (!field || !field.editable || !field.backendKey) return;
        let value;
        if (field.type === "objectSelect" || field.type === "employee") {
          const obj = this.form[key];
          value = obj ? this.getPath(obj, field.idPath) : null;
        } else {
          value = this.form[key];
          if (field.type === "number") value = value === "" || value === null ? null : Number(value);
          else if (field.type === "boolean") value = !!value;
          else if (value === "") value = null;
        }
        this.setPath(payload, field.backendKey, value);
      });
      return payload;
    },

    // Encode the active filters the way getAssignments does, so the batch-update
    // endpoint scopes to exactly the records the table is showing.
    buildRequestQuery() {
      const pairs = Object.keys(this.filterQuery || {})
        .filter((key) => {
          const value = this.filterQuery[key];
          return value !== undefined && value !== null && value !== "";
        })
        .map((key) => `${key}=${this.filterQuery[key]}`);
      return pairs.length ? `?${pairs.join("&")}` : "";
    },

    async publishChanges() {
      const payload = this.buildPayload();
      if (!Object.keys(payload).length) return;

      // "All matching": one query-scoped PATCH covers every filtered record,
      // including the ones not loaded on this page.
      if (this.bulkQueryEdit) {
        this.processing = true;
        try {
          const resp = await updateAssignmentsList({
            payload,
            requestQuery: this.buildRequestQuery(),
          });
          this.handleFlash({ response: resp, show: true });
          if (!resp || resp.status > 204) return;
          this.$emit("batch-updated");
        } catch (err) {
          console.error(err);
          this.handleFlash({ response: err, show: true });
        } finally {
          this.processing = false;
        }
        return;
      }

      this.processing = true;
      try {
        const responses = await Promise.all(
          this.rows.map((row) =>
            // Mirrors the V1 bulk edit: PATCH /assignment/part-update/{id}.
            processAssignment({ data: payload, id: row.id, batchEdit: true })
          )
        );
        const failed = responses.filter((r) => !r || r.status > 204);
        if (failed.length) {
          this.handleFlash({
            response: {
              data: {
                statusCode: 500,
                error: `Failed to update ${failed.length} of ${this.rows.length} assignments.`,
              },
            },
            show: true,
          });
        } else {
          this.handleFlash({ response: { status: 200 }, show: true });
        }
        this.$emit("batch-updated");
      } catch (err) {
        console.error(err);
        this.handleFlash({ response: err, show: true });
      } finally {
        this.processing = false;
      }
    },

    closeForm() {
      this.$emit("close-batch-edit");
    },
  },
};
</script>

<style lang="scss" scoped>
.batch-edit {
  font-family: "Roboto", sans-serif !important;
  border-radius: 15px !important;
  min-width: 60vw;

  &__title {
    border-bottom: 1px solid #e0e0e0;
    min-height: 50px;
    padding: 8px 20px;
    &--left {
      font-size: 16px;
      font-weight: 600;
      color: #656f7d;
    }
    &--right {
      justify-content: flex-end;
    }
    &--discard {
      font-size: 15px;
      font-weight: 400;
      color: #1976d2;
      padding: 4px 10px;
      border: 1px solid #1976d2;
      border-radius: 4px;
      &:hover:not(:disabled) {
        background-color: #e3f2fd;
        cursor: pointer;
      }
      &:disabled {
        opacity: 0.5;
      }
    }
    &--publish {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 5px 11px;
      background-color: #1976d2;
      border: 1px solid #1976d2;
      border-radius: 4px;
      color: #fff;
      font-weight: 400;
      font-size: 15px;
      &:hover:not(:disabled) {
        cursor: pointer;
        opacity: 0.9;
      }
      &:disabled {
        opacity: 0.5;
      }
    }
    &--delimetr {
      width: 1px;
      height: 28px;
      background-color: #e0e0e0;
    }
    &--icon {
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &--close {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  // v-card__text ships its own typography; the design uses the form's instead.
  &__body {
    padding: 24px 40px 40px !important;
    font-size: 16px !important;
    color: #292a2c !important;
  }

  &__tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    .tab-btn {
      padding: 10px 16px;
      border: none;
      background: transparent;
      font-weight: 500;
      font-size: 14px;
      text-transform: uppercase;
      color: #0000008a;
      border-bottom: 2px solid transparent;
      &.active {
        color: #1976d2;
        border-bottom: 2px solid #1976d2;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  &__fields {
    display: flex;
    gap: 15px;
    padding-bottom: 20px;
  }

  &__column {
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 40px;
    width: 100%;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 100%;
    width: 170px;
    flex-shrink: 0;
    padding-left: 2px;
    overflow: hidden;
    &--wide {
      width: 220px;
    }
    &--inherit {
      width: 13px;
      height: 12px;
      flex-shrink: 0;
      margin-right: -2px;
    }
    &--icon {
      width: 18px !important;
      height: 18px !important;
      font-size: 18px !important;
      flex-shrink: 0;
      color: #656f7d !important;
    }
    &--text {
      font-size: 16px;
      font-weight: 400;
      line-height: 18px;
      color: #656f7d;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &--optional {
      font-size: 14px;
      font-weight: 300;
      font-style: italic;
    }
  }

  &__value {
    flex: 1 0 0;
    min-width: 0;
    overflow: hidden;
  }

  &__section-title {
    margin: 36px 0 24px;
    color: #0000008a;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 15px 10px 10px;
    border-radius: 6px;
    background-color: #f5f5f5;
  }

  &__timezones {
    display: flex;
    gap: 5px;
    align-items: center;
    height: 40px;
  }

  &__tz-chip {
    display: flex;
    align-items: center;
    height: 23px;
    padding: 0 5px;
    border-radius: 5px;
    &--active {
      background-color: #d9d9d9;
    }
    &--label {
      font-size: 16px;
      line-height: 18px;
      color: #656f7d;
      white-space: nowrap;
      cursor: pointer;
    }
    &--arrow {
      font-size: 24px !important;
      color: #656f7d !important;
    }
  }

  &__panel-grid {
    display: flex;
    gap: 15px;
  }

  &__panel-col {
    flex: 1 0 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  &__panel-actions {
    display: flex;
    justify-content: flex-end;
  }

  &__table {
    display: flex;
    flex-direction: column;
    width: 100%;
    &--row {
      display: flex;
      width: 100%;
      min-height: 47px;
      background-color: #fff;
      border-bottom: 1px solid #0000001f;
      font-size: 14px;
      line-height: 21px;
      color: #000000de;
    }
    &--cell {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0 16px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    &--head {
      font-weight: 700;
      line-height: 18px;
      color: #6c737a;
    }
    &--total {
      font-weight: 700;
    }
    &--reference {
      text-decoration: underline;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    // Column widths taken straight from the design's 980px table grids.
    &--steps &--cell {
      &:nth-child(1) { width: 27.65%; }
      &:nth-child(2) { width: 19.59%; }
      &:nth-child(3) { width: 25.51%; }
      &:nth-child(4) { width: 27.25%; }
    }
    &--worktypes &--cell {
      &:nth-child(1) { width: 13.06%; }
      &:nth-child(2) { width: 16.94%; }
      &:nth-child(3) { width: 13.67%; }
      &:nth-child(4) { width: 12.35%; }
      &:nth-child(5) { width: 13.16%; }
      &:nth-child(6) { width: 11.12%; }
      &:nth-child(7) { width: 13.27%; }
      &:nth-child(8) { width: 6.43%; }
    }
  }
}

.batch-field {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  width: 100%;
  padding: 0 8px;
  border-radius: 4px;
  position: relative;
  &--clickable {
    cursor: pointer;
    &:hover {
      background-color: #f2f2f2;
    }
  }
  &--locked {
    cursor: not-allowed;
  }
  &--readonly {
    cursor: default;
  }
  // Inside the grey panels the design draws the control as a white box.
  &--boxed {
    background-color: #fff;
    cursor: pointer;
  }
  &__text {
    font-size: 16px;
    line-height: 18px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &--filled {
    color: #000;
  }
  &--placeholder {
    color: #b4b4b4;
  }
  &__link {
    font-size: 16px;
    line-height: 18px;
    color: #0000ee !important;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__input {
    flex: 1 0 0;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 16px;
    line-height: 18px;
    color: #000;
    &::placeholder {
      color: #b4b4b4;
    }
  }
  &__status-chip {
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    color: #fff;
    cursor: pointer;
  }
  &__priority-icon {
    width: 12px;
    height: 12px;
    font-size: 16px !important;
    margin-right: 4px;
  }
  &__initials {
    min-width: 36px;
    height: 36px;
    font-size: 16px;
    font-weight: 400;
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__dropdown-chip {
    border-radius: 50% !important;
    height: 40px !important;
  }
  &__checkbox {
    display: inline-flex;
  }
  &__clear {
    width: 16px;
    height: 16px;
    padding: 4px;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 8px;
    z-index: 5;
    &:hover {
      background-color: #dedede;
      cursor: pointer;
    }
  }
  &__list {
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>
